import type {
  CalendarRangeQuery,
  ConversationListQuery,
  MessageListQuery,
  MessageSortOrder,
  TaskSmartView,
} from './types';

/**
 * The one place query keys are constructed. Nothing else in the app writes a
 * key literal — see docs/plan/06-frontend-v1.md §1 ("Query key factory").
 *
 * ## Account scoping
 *
 * **Every key that can hold account data is prefixed with `['account', id]`.**
 * That is not cosmetic: the app is multi-account, a key that forgets its
 * account is a cross-account data leak (account B rendering account A's rows
 * because both hashed to the same key), and the prefix is what makes an account
 * switch a cache read instead of a refetch. It also makes logout a single
 * `removeQueries({ queryKey: queryKeys.account(id).all() })`.
 *
 * The design sketch in §1 wrote message detail as `['message', id]`. It is
 * account-scoped here for exactly the reason above: message ids are only
 * unique *within* an account, so an unscoped detail key is the leak the rest of
 * the factory exists to prevent.
 *
 * ## Shape
 *
 * Keys are hierarchical, so a prefix invalidates/patches everything below it:
 *
 *   ['account', id]                                 → everything for an account
 *   ['account', id, 'messages']                     → every message list
 *   ['account', id, 'messages', { folderId, ... }]  → one list
 *   ['account', id, 'message']                      → every message detail
 *   ['account', id, 'message', messageId]           → one detail
 *
 * Filter objects are hashed structurally by TanStack Query (key order does not
 * matter), so the "scope" entries above are safe prefixes for `setQueriesData`.
 */

/**
 * Filters that identify one message list. Mirrors `MessageListQuery` minus paging.
 *
 * A search across folders (`scope`) lives under the same account prefix, so
 * every cache patch that reaches a folder list reaches it too. Its `folderId`
 * is `''`: the result list does not depend on the folder the search started
 * from, so opening a result from another folder keeps it.
 */
export type MessageListFilters = Omit<MessageListQuery, 'accountId' | 'cursor' | 'limit'> & {
  /** Set only for a cross-folder search: `all`, or `in` for the query's `in:` terms. */
  scope?: 'all' | 'in';
  includeTrashJunk?: boolean;
};

/**
 * Filters that identify one *merged* message list (ADR 0056).
 *
 * `accounts` is the account set, joined and sorted, so the merged list of two
 * mailboxes and the merged list of three are different queries rather than one
 * query with a stale answer.
 */
export interface UnifiedListFilters {
  accounts: string;
  sort?: MessageSortOrder;
  search?: string;
  /** Every lane searches all of its mailbox's folders, not only its Inbox. */
  allFolders?: boolean;
  includeTrashJunk?: boolean;
  unreadOnly?: boolean;
  flaggedOnly?: boolean;
}

/**
 * Filters that identify one conversation list (ADR 0077). Mirrors
 * `ConversationListQuery` minus paging; there is no search — search results
 * are messages.
 */
export type ConversationListFilters = Omit<ConversationListQuery, 'accountId' | 'cursor' | 'limit'>;

/** Filters that identify one calendar range query. */
export type CalendarRangeFilters = Omit<CalendarRangeQuery, 'accountId'>;

/** Filters that identify one contact list query. */
export interface ContactListFilters {
  addressBookId: string;
  search?: string;
  /** A group card's id (#123): its own cache entry, so no group shows another's rows. */
  group?: string;
}

/**
 * Filters that identify one "is this address already a contact?" lookup.
 *
 * Both parts are pre-folded (trimmed, lower-cased) by the caller so two
 * spellings of the same header — `Finance <Finance@Example.test>` and
 * `finance <finance@example.test>` — are one cache entry rather than two.
 */
export interface ContactLookupFilters {
  email: string;
  /** `''` when the header carried no display name. */
  name: string;
}

export const queryKeys = {
  /** The session bootstrap (`GET /auth/session`). Not account-scoped by nature. */
  session: () => ['session'] as const,

  /**
   * The owner's live sessions (`GET /auth/sessions`, ADR 0045).
   *
   * Session-scoped, not account-scoped: a session belongs to the owner, and
   * the owner has one identity across every connected mailbox.
   */
  sessions: () => ['session', 'list'] as const,

  /** The account list itself — the one legitimately account-less mail key. */
  accounts: () => ['accounts'] as const,

  /**
   * The external-provider catalogue (`GET /accounts/providers`, ADR 0107).
   * Server configuration, not mail: a root of its own so invalidating the
   * account list does not refetch it.
   */
  accountProviders: () => ['account-providers'] as const,

  /** Every key below this belongs to exactly one account. */
  account: (accountId: string) => ({
    /** Prefix for the whole account: invalidate/remove on logout or removal. */
    all: () => ['account', accountId] as const,

    quota: () => ['account', accountId, 'quota'] as const,
    folders: () => ['account', accountId, 'folders'] as const,
    /** The roles chosen in Settings (#68); separate from the tree it explains. */
    folderRoles: () => ['account', accountId, 'folder-roles'] as const,

    /** Prefix matching every message list of this account, whatever the filters. */
    messagesScope: () => ['account', accountId, 'messages'] as const,
    messages: (filters: MessageListFilters) =>
      ['account', accountId, 'messages', filters] as const,

    /**
     * Every conversation list of this account (ADR 0077). A root of its own,
     * not a spelling under `messagesScope()`: its pages hold conversations,
     * not messages, and the message patches must never see them. The
     * conversation patches in `cache/conversationPatches.ts` reach it instead.
     */
    conversationsScope: () => ['account', accountId, 'conversations'] as const,
    conversations: (filters: ConversationListFilters) =>
      ['account', accountId, 'conversations', filters] as const,

    /** Prefix matching every cached message detail of this account. */
    messageScope: () => ['account', accountId, 'message'] as const,
    /**
     * One message detail.
     *
     * `remote` (ADR 0018) is part of the key because a body rendered with
     * remote content blocked and the same body rendered with it loaded are two
     * different documents, not one document refetched: opting in re-requests
     * with `?remote=load` and the frame is rebuilt from scratch under a new
     * key rather than mutated in place. Both spellings sit under
     * `messageScope()`, so a flag patch still reaches either.
     */
    message: (messageId: string, remote?: 'block' | 'load') =>
      remote === undefined
        ? (['account', accountId, 'message', messageId] as const)
        : (['account', accountId, 'message', messageId, remote] as const),
    /**
     * An email attached to a message (ADR 0092). Its own root, never under
     * `messageScope()`: it has no flags for a detail patch to reach, and the
     * offline cache (which persists only message details) must not keep it.
     */
    attached: (messageId: string, path: string, remote?: 'block' | 'load') =>
      ['account', accountId, 'attached', messageId, path, remote ?? 'auto'] as const,
    /** Raw RFC822 source lives under its own root so a detail patch never sees a string. */
    messageRaw: (messageId: string) => ['account', accountId, 'message-raw', messageId] as const,

    drafts: () => ['account', accountId, 'drafts'] as const,

    calendars: () => ['account', accountId, 'calendars'] as const,
    /**
     * One calendar's named share links.
     *
     * Its own root rather than a spelling under `calendars()`: the listing is
     * a different read with a different lifetime, and it is fetched only while
     * the sharing dialog is open.
     *
     * What is cached here is the *metadata* — label, mode, timestamps. The
     * minted URL is never put in any cache: it is a bearer credential for a
     * whole calendar, this app persists its query cache, and a credential on
     * disk outlives the dialog that was careful with it.
     */
    calendarShareLinks: (calendarId: string) =>
      ['account', accountId, 'calendar-share-links', calendarId] as const,
    eventsScope: () => ['account', accountId, 'events'] as const,
    events: (filters: CalendarRangeFilters) => ['account', accountId, 'events', filters] as const,

    addressBooks: () => ['account', accountId, 'address-books'] as const,
    contactsScope: () => ['account', accountId, 'contacts'] as const,
    contacts: (filters: ContactListFilters) => ['account', accountId, 'contacts', filters] as const,
    contact: (contactId: string) => ['account', accountId, 'contact', contactId] as const,
    /**
     * Every addressable contact, flattened for the composer's recipient
     * autocomplete. Deliberately *not* under `contactsScope()`: a contact
     * write invalidates the paginated lists it can patch precisely, and this
     * is a different read with a different lifetime.
     */
    recipientDirectory: () => ['account', accountId, 'recipient-directory'] as const,

    /**
     * Address lookups from a message header (`Contact[]`, not a page).
     *
     * Its own root rather than a spelling under `contactsScope()`: the list
     * surgery in `features/contacts/cache.ts` walks everything under that
     * prefix expecting `{ items }` pages, and a bare array there would be
     * handed to it as a page. Invalidation still reaches this — it is listed
     * in `contactAffectedKeys` — which is what makes a card saved from the
     * reading pane visible to the next lookup.
     */
    contactLookupScope: () => ['account', accountId, 'contact-lookup'] as const,
    contactLookup: (filters: ContactLookupFilters) =>
      ['account', accountId, 'contact-lookup', filters] as const,

    sieveRules: () => ['account', accountId, 'sieve', 'rules'] as const,
    sieveScripts: () => ['account', accountId, 'sieve', 'scripts'] as const,
    /**
     * The newest messages of one folder, offered as samples by the filter
     * preview (#140). Its own root, not under `messages`: those entries are the
     * mail list's infinite queries, which the live patches rewrite.
     */
    sieveSamples: (folderId: string) =>
      ['account', accountId, 'sieve', 'samples', folderId] as const,
    /** The out-of-office card (its own block in the same script). */
    sieveVacation: () => ['account', accountId, 'sieve', 'vacation'] as const,
    /**
     * The copies of the script kept before every write (Settings → Filters →
     * Previous versions). EVERY Sieve write adds one — filters, raw script,
     * block/unblock, out of office, label migrations, a restore — so each of
     * those must invalidate this as well. One backup's text sits under it, so
     * invalidating the list reaches the open one too.
     */
    sieveBackups: () => ['account', accountId, 'sieve', 'backups'] as const,
    sieveBackup: (backupId: number) =>
      ['account', accountId, 'sieve', 'backups', backupId] as const,
    /**
     * Blocked senders. A view over the Sieve rules, but its own root: a block
     * written here must invalidate `sieveRules()` as well, and the reverse.
     */
    blockedSenders: () => ['account', accountId, 'blocked-senders'] as const,

    notificationPrefs: () => ['account', accountId, 'notification-prefs'] as const,
    /** Sender-layer notification rules (ADR 0021). */
    notificationRules: () => ['account', accountId, 'notification-rules'] as const,
    /**
     * The rules preview. `limit` is part of the key because a preview of 50 and
     * a preview of 10 are different answers, not the same answer truncated.
     */
    notificationPreview: (limit: number) =>
      ['account', accountId, 'notification-preview', limit] as const,
    /**
     * The latest answer from the interim new-mail poll.
     *
     * Cached under a key so every window can read the server's own
     * `poll_after_seconds` and `interim` flag, including the windows that are
     * not the polling leader and the Settings page, which needs `interim` to
     * decide whether the "checked every minute" line still tells the truth.
     */
    newMailPoll: () => ['account', accountId, 'new-mail-poll'] as const,
    /** Persisted remote-content grants (ADR 0018). */
    remoteContentRules: () => ['account', accountId, 'remote-content-rules'] as const,

    /**
     * Tasks (ADR 0069). One prefix for everything, because one change usually
     * reaches several reads at once — completing a starred task changes its
     * list, the list's counts and the Starred view — and the live update
     * (`tasks_changed`) names only the account, so it invalidates the prefix.
     */
    tasksScope: () => ['account', accountId, 'tasks'] as const,
    taskLists: () => ['account', accountId, 'tasks', 'lists'] as const,
    tasks: (listId: string) => ['account', accountId, 'tasks', 'list', listId] as const,
    taskSmart: (view: TaskSmartView, dueBefore: string) =>
      ['account', accountId, 'tasks', 'smart', view, dueBefore] as const,
    /** The tasks made from one email (ADR 0075): the reading pane's "In Tasks" chip. */
    tasksByEmail: (rfcMessageId: string, messageId: string) =>
      ['account', accountId, 'tasks', 'by-email', rfcMessageId, messageId] as const,

    /**
     * Send later and Snooze (ADR 0076). One prefix, because an enrolment
     * changes the status and every schedule or snooze changes a list.
     */
    /**
     * The mailbox's labels (plan/17 §5), refetched on `labels_changed`. One
     * list per account: a label never spans mailboxes.
     */
    labels: () => ['account', accountId, 'labels'] as const,

    schedulingScope: () => ['account', accountId, 'scheduling'] as const,
    schedulingStatus: () => ['account', accountId, 'scheduling', 'status'] as const,
    scheduledSends: () => ['account', accountId, 'scheduling', 'scheduled'] as const,
    snoozes: () => ['account', accountId, 'scheduling', 'snoozes'] as const,

    /**
     * Copies of sent mail not in Sent yet (#84), and whether this mailbox saves
     * one at all. Refetched after every send that reports a `sentCopy`.
     */
    sentCopies: () => ['account', accountId, 'sent-copies'] as const,
    sentSettings: () => ['account', accountId, 'sent-settings'] as const,
  }),

  /**
   * The unified mail view's merged list (ADR 0056) — the one key that
   * deliberately holds rows from several accounts at once.
   *
   * It is the documented exception to the account-scoping rule above, and it
   * is safe for exactly two reasons, both of which must stay true:
   *
   * 1. **Every row carries its own `accountId`** (`MessageSummary.accountId`),
   *    so nothing in the list is ambiguous about whose mail it is.
   * 2. **Every patch into it matches on that `accountId` as well as the id.**
   *    A message id embeds no account (ADR 0012), so the same id string can
   *    name a different message in two mailboxes — patching this list by id
   *    alone would touch the wrong row. See `src/cache/patches.ts`.
   *
   * `filters` carries the account set, so attaching or detaching a mailbox is
   * a different list rather than a stale one.
   */
  unifiedMessagesScope: () => ['unified', 'messages'] as const,
  unifiedMessages: (filters: UnifiedListFilters) => ['unified', 'messages', filters] as const,

  /**
   * The merged list in conversation view (ADR 0077): the same lane merge,
   * over conversations. The same two rules as `unifiedMessages` hold — every
   * row carries its `accountId`, and every patch matches on it.
   */
  unifiedConversationsScope: () => ['unified', 'conversations'] as const,
  unifiedConversations: (filters: UnifiedListFilters) => ['unified', 'conversations', filters] as const,

  /** Push subscriptions are per-device/session, not per-account. */
  pushSubscriptions: () => ['push', 'subscriptions'] as const,

  /** Settings → General: per owner, so neither account-scoped nor per device. */
  preferences: () => ['preferences'] as const,

  /** Settings → AI assistant (ADR 0065): per owner, like preferences. */
  aiSettings: () => ['ai', 'settings'] as const,
  /** `GET /ai/models`: what the saved key can use. Refetched when the key changes. */
  aiModels: () => ['ai', 'models'] as const,

  /** Nextcloud (ADR 0084): the link, per owner like the assistant. `GET /nextcloud`. */
  nextcloud: () => ['nextcloud', 'status'] as const,
  /** "Files shared from mail": every page of `GET /nextcloud/uploads`. */
  nextcloudUploads: () => ['nextcloud', 'uploads'] as const,
  /** A folder of the linked Nextcloud, for the compose picker. */
  nextcloudFiles: (path: string) => ['nextcloud', 'files', path] as const,
} as const;

export type QueryKeys = typeof queryKeys;
/** The literal type of any key this factory produces. */
export type AppQueryKey =
  | ReturnType<QueryKeys['session']>
  | ReturnType<QueryKeys['sessions']>
  | ReturnType<QueryKeys['accounts']>
  | ReturnType<QueryKeys['accountProviders']>
  | ReturnType<QueryKeys['unifiedMessagesScope']>
  | ReturnType<QueryKeys['unifiedMessages']>
  | ReturnType<QueryKeys['unifiedConversationsScope']>
  | ReturnType<QueryKeys['unifiedConversations']>
  | ReturnType<QueryKeys['pushSubscriptions']>
  | ReturnType<QueryKeys['preferences']>
  | ReturnType<QueryKeys['aiSettings']>
  | ReturnType<QueryKeys['aiModels']>
  | ReturnType<QueryKeys['nextcloud']>
  | ReturnType<QueryKeys['nextcloudUploads']>
  | ReturnType<QueryKeys['nextcloudFiles']>
  | ReturnType<ReturnType<QueryKeys['account']>[keyof ReturnType<QueryKeys['account']>]>;
